// src/pages/cash/CashList.tsx

import React, { useState } from "react";
import { EDataTable } from "../../../components/EDataTable";
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { TableActions } from "../../../components/TableActions";
import { Edit, Trash2, DollarSign } from "lucide-react"; 
import { CashDialog } from "./CashDialog"; 
import { toast } from "react-toastify";

// Tipler ve Hook'lar
import { Cash, CashPayload } from "../../../types/cash";  
import { useCashStore } from "../../../store/cashStore";  
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { DataTableColumn, SimpleFilter } from "../../../types"; 


export const CashList = () => {
    const store = useCashStore();
    const { createItem, updateItem, deleteItem } = store; 
    const { handleApiError } = useApiErrorHandler();
    
    // --- STATE'LER ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [cashToEdit, setCashToEdit] = useState<Cash | null>(null);

    // --- ACTION HANDLERS (Aynı CRUD Mantığı) ---
    const handleNew = () => {
        setCashToEdit(null);
        setIsModalOpen(true);
    };

    const handleEdit = (cash: Cash) => {
        setCashToEdit(cash);
        setIsModalOpen(true);
    };

    const { confirm, modal: confirmModal } = useConfirmModal();

    const handleDeleteClick = async (cash: Cash) => {
        const ok = await confirm(`${cash.name} adlı kasayı silmek istiyor musun? Bu kasaya bağlı tüm hareketler kontrol edilmeli!`);

        if (ok) {
            try {
                await deleteItem(cash.id);
                toast.success("Kasa silindi");
            } catch (error) {
                handleApiError(error); 
            }
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCashToEdit(null);
    };

    // CashPayload tipine uygun veriyi alır
    const handleSaveCash = async (cashData: CashPayload) => { 
        const cleanedData: any = { ...cashData };
 
        try {
            if (cashToEdit) {
                await updateItem(cashToEdit.id, cleanedData);
                toast.success(`Kasa Güncellendi: ${cleanedData.name}`);
            } else {
                await createItem(cleanedData);
                toast.success(`Yeni Kasa Eklendi: ${cleanedData.name}`);
            }
            
        } catch (error) {
            handleApiError(error); 
        }
        handleCloseModal(); 
    };

    // --- DataTable body templates ---
    
    // Bakiye formatlama
    const balanceBodyTemplate = (cash: Cash) => (
        <span className={`font-semibold ${cash.balance >= 0 ? 'text-green-700' : 'text-red-600'}`}>
             {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: cash.currency || 'TRY' }).format(cash.balance)}
        </span>
    );
    
    const actionBodyTemplate = (cash: Cash) => (
        <TableActions
            actions={[
                {
                    icon: Edit,
                    colorClass: "text-blue-600 hover:bg-blue-50",
                    title: "Düzenle",
                    onClick: () => handleEdit(cash)
                },
                {
                    icon: Trash2,
                    colorClass: "text-red-500 hover:bg-red-50",
                    title: "Sil",
                    onClick: () => handleDeleteClick(cash)
                }
            ]}
        />
    );

    // Kasa sütun tanımları
    const cashColumns: DataTableColumn<Cash>[] = [
        { field: "id", header: "ID", width: "80px" },
        { 
            field: "name", 
            header: "Kasa Adı", 
            width: "250px", 
            sortable: true, 
        },
        { 
            field: "currency", 
            header: "Para Birimi", 
            width: "150px", 
            sortable: true,
        },
        { 
            field: "balance", 
            header: "Mevcut Bakiye", 
            width: "180px", 
            align: "right",
            body: balanceBodyTemplate,
            sortable: true,
        },
        { 
            field: "actions", 
            header: "İşlemler", 
            width: "120px", 
            align: "center",
            body: actionBodyTemplate 
        },
    ];

    return (
        <div className="w-full">
            {confirmModal}
            <EDataTable<Cash> 
            tableKey="cash_list"
                useStore={() => store} 
                dataKey="id"
                title="Kasa Listesi"
                placeholder="Kasa Adı Ara"
                searchFields={[
                    { field: "name", operator: "contains" as SimpleFilter["operator"] },
                ]}
                actions={
                    <TextButton onClick={handleNew} className="btn-new">
                         Yeni Kasa Ekle
                    </TextButton>
                }
                columns={cashColumns}
            />

            <CashDialog
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                cash={cashToEdit}
                onSave={handleSaveCash}
            />
        </div>
    );
};